/**
 * Auth hooks - React context and hooks for authentication
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { Profile, TradieProfile, UserRole } from '@/lib/types';
import { clearTenantCache } from '@/lib/tenant';
import { supabase } from '@/integrations/supabase/client';
import * as authQueries from '../queries';
import { identifyUser, resetUser } from '@/lib/analytics';
import { setSentryUser, clearSentryUser } from '@/lib/sentry';

const DEBUG = import.meta.env.DEV;
function log(...args: unknown[]) {
  if (DEBUG) console.log('[Auth]', ...args);
}

/** Remove only Supabase auth keys from storage, preserving other app state. */
export function clearSupabaseStorage() {
  const supabaseKeys = Object.keys(localStorage).filter(
    (key) => key.startsWith('sb-') && key.endsWith('-auth-token')
  );
  for (const key of supabaseKeys) {
    localStorage.removeItem(key);
  }
  // Supabase may also use sessionStorage for PKCE flow
  const sessionKeys = Object.keys(sessionStorage).filter(
    (key) => key.startsWith('sb-') || key.startsWith('supabase')
  );
  for (const key of sessionKeys) {
    sessionStorage.removeItem(key);
  }
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  tradieProfile: TradieProfile | null;
  isLoading: boolean;
  userRole: UserRole | null;
  isApproved: boolean;
  signUp: (email: string, password: string, fullName: string, role: UserRole) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [tradieProfile, setTradieProfile] = useState<TradieProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [isApproved, setIsApproved] = useState(false);

  /**
   * Fetch profile + tradie profile and write to state.
   *
   * Gated by an `isCancelled` getter at every state-setter boundary. This
   * lets the caller's cleanup (effect unmount, sign-out, sign-in-as-different-
   * user) abort an in-flight fetch — including its retry timer — and
   * guarantees no stale profile bleeds into state after the user changes
   * or the provider unmounts.
   */
  const fetchProfile = async (
    userId: string,
    isCancelled: () => boolean,
    retries = 1,
  ): Promise<void> => {
    log('fetchProfile', userId, 'retries left:', retries);
    try {
      const profileData = await authQueries.fetchProfile(userId);
      if (isCancelled()) return;
      log('fetchProfile result:', profileData ? { role: profileData.role, is_approved: profileData.is_approved } : null);

      if (!profileData) {
        log('No profile found for user', userId);
        return;
      }

      if (isCancelled()) return;
      setProfile(profileData);

      if (isCancelled()) return;
      setUserRole(profileData.role);

      if (isCancelled()) return;
      setIsApproved(profileData.is_approved ?? false);

      if (isCancelled()) return;
      identifyUser(userId, {
        email: profileData.email,
        role: profileData.role,
        created_at: profileData.created_at,
      });

      if (isCancelled()) return;
      setSentryUser({ id: userId, email: profileData.email, role: profileData.role });

      if (profileData.role === 'tradie') {
        try {
          const tradieData = await authQueries.fetchTradieProfile(userId);
          if (isCancelled()) return;
          if (tradieData) {
            if (isCancelled()) return;
            setTradieProfile(tradieData);
          }
        } catch {
          // Non-critical — tradie profile is supplementary
          log('tradie profile fetch failed (non-critical)');
        }
      }
    } catch (error) {
      if (isCancelled()) return;
      console.error('[Auth] Error fetching profile:', error);
      if (retries > 0) {
        await new Promise((r) => setTimeout(r, 1000));
        if (isCancelled()) return;
        return fetchProfile(userId, isCancelled, retries - 1);
      }
      // After retries exhausted, leave profile null but do NOT sign out.
      // Signing out here causes a race: a transient network error during
      // token refresh would nuke the session mid-use.
      log('Profile fetch failed after retries — profile will be null');
    }
  };

  /** Nuclear sign-out: clears everything and forces redirect */
  const hardSignOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch {
      // Ignore — we're clearing everything anyway
    }
    clearTenantCache();
    setUser(null);
    setSession(null);
    setProfile(null);
    setTradieProfile(null);
    setUserRole(null);
    setIsApproved(false);
  };

  // ── Effect 1: sync-only auth listener ────────────────────────────────
  // Supabase invokes the onAuthStateChange callback while the auth-token
  // lock is held. ANY async work or Supabase call inside this callback
  // re-enters the lock and deadlocks (5s LockAcquireTimeout warning,
  // then borked client state — every downstream getSession hangs).
  //
  // This callback MUST stay synchronous. Profile fetching is owned by
  // Effect 2 below, keyed on user.id, with proper cancellation. Do not
  // make this callback async or await anything inside it.
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        log('onAuthStateChange', event, newSession?.user?.email ?? 'no user');
        setSession(newSession);
        setUser(newSession?.user ?? null);

        if (!newSession?.user) {
          clearTenantCache();
          setProfile(null);
          setTradieProfile(null);
          setUserRole(null);
          setIsApproved(false);
        }
      },
    );

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // ── Effect 2: profile fetch driven by user.id changes ────────────────
  // Cancellation closure prevents stale writes if the user changes (or
  // signs out) while a fetch is in flight, and prevents the retry timer
  // from spawning a fresh fetch on a cancelled flow.
  //
  // Behavioural change vs. the prior implementation: TOKEN_REFRESHED
  // events that don't change user.id no longer trigger a profile refetch.
  // This is intentional — JWT rotation doesn't change the underlying
  // profile row, so the previous re-fetch was wasted work and was a
  // contributor to the auth-token-lock deadlock.
  useEffect(() => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    let cancelled = false;
    const isCancelled = () => cancelled;

    fetchProfile(user.id, isCancelled).finally(() => {
      if (!cancelled) setIsLoading(false);
    });

    return () => {
      cancelled = true;
    };
    // fetchProfile is a stable closure over setState (which React
    // guarantees stable) and module-level imports; safe to omit from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const signUp = async (email: string, password: string, fullName: string, role: UserRole) => {
    const { error } = await authQueries.signUp(email, password, fullName, role);
    return { error: error as Error | null };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await authQueries.signIn(email, password);
    return { error: error as Error | null };
  };

  const signOut = async () => {
    log('signOut called');
    resetUser(); // Clear PostHog identity before clearing auth
    clearSentryUser(); // Clear Sentry user context
    await hardSignOut();
    // Remove Supabase auth keys only — preserve other app/third-party state
    clearSupabaseStorage();
  };

  const refreshProfile = async () => {
    if (user) {
      // Manual user-initiated refresh — no cancellation token needed
      // because the caller chose to refresh and any concurrent fetch
      // races resolve last-writer-wins.
      await fetchProfile(user.id, () => false);
    }
  };

  return React.createElement(
    AuthContext.Provider,
    {
      value: {
        user,
        session,
        profile,
        tradieProfile,
        isLoading,
        userRole,
        isApproved,
        signUp,
        signIn,
        signOut,
        refreshProfile,
      },
    },
    children
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
